/**
 * FO6 Structure Loss Settlement and Roof Loss Settlement Business Logic Utilities
 *
 * This file contains all business logic for FO-6 Structures Loss Settlement options
 * and Roof Loss Settlement options, including structure category constraints and
 * role-based access control.
 *
 * Business Rules Implementation:
 *
 * Structure Category Rules:
 * - (a) Confinement Buildings: RC and ACV options for both Structure and Roof Loss Settlement
 * - (b) Dwelling and Contents: ACV only for both
 * - (c) Fencing: ACV only for Structure Loss Settlement, no Roof Loss Settlement field
 * - (d) Hoop Buildings: ACV only for both
 * - (e) Poultry Buildings: ACV and RC options for both
 * - (f) Silos and Grain Bins: underwriters see RC options, everyone else sees ACV only
 * - (g) Barns/Outbuildings: Building class logic (1/2=RC, 3=ACV)
 *
 * Role-Based Access Control:
 * - Agents: ACV only for Confinement, Poultry, and Silos/Grain Bins
 * - Underwriters: Both ACV and RC options (with "Requires UW Approval" labels)
 *
 * Interdependency Rules:
 * - When Structure Loss Settlement is ACV, Roof Loss Settlement must also be ACV
 * - When Structure Loss Settlement is RC, Roof Loss Settlement can be ACV or RC
 *
 * Related GitHub Issues:
 * - Original implementation: #28408
 * - Structure category validation: #28408v2
 *
 * @file FO6.utils.js
 * @version 2.0
 */

/**
 * Validates 80% replacement cost requirement for RC selections
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {Object} params.building - Building data with replacementCostEstimate
 * @param {boolean} [params.isNewRisk=false] - Whether this is a new (unrated) risk
 * @returns {Object} Validation result with isValid and message
 */
export const validate80PercentRC = ({ coverages, building, isNewRisk = false }) => {
  if (
    coverages?.structureLossSettlement === 'RC' ||
    coverages?.roofLossSettlement === 'RC' ||
    coverages?.roofLossSettlement === 'RC_ACV_WIND_HAIL'
  ) {
    const actualReplacementCost = building?.replacementCostEstimate?.total || 0;

    // For new risks, allow RC selection without RCE validation
    if (isNewRisk && actualReplacementCost === 0) {
      return {
        isValid: true,
        message: '✓ RC loss settlement selected. Validation will apply after rating.'
      };
    }

    // Existing validation logic for rated risks
    if (actualReplacementCost > 0) {
      const coverageE = coverages?.coverageE || 0;
      const minimumRequired = actualReplacementCost * 0.8;

      if (coverageE < minimumRequired) {
        return {
          isValid: false,
          message: `Coverage E must be at least 80% of replacement cost ($${minimumRequired.toLocaleString()}) when RC is selected. Current RCE: $${actualReplacementCost.toLocaleString()}.`
        };
      }

      return {
        isValid: true,
        message: `✓ Coverage E meets 80% RC requirement ($${minimumRequired.toLocaleString()} minimum).`
      };
    }

    // Only show error for existing risks without RCE data
    return {
      isValid: false,
      message: 'RC loss settlement selected but RCE data not available for validation.'
    };
  }

  return { isValid: true, message: '' };
};

/**
 * Gets wind & hail deductible options with calculated dollar amounts
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {boolean} params.isDwellingContents - Whether structure is dwelling/contents type
 * @returns {Array} Array of wind hail options with id and value
 */
export const getWindHailOptions = ({ coverages, isDwellingContents }) => {
  // Get current coverage amounts for calculation
  const coverageE = isDwellingContents ? coverages?.dwellingLimit || 0 : coverages?.coverageE || 0;
  const deductibleAllOther = coverages?.deductibleAllOther ?? 1500;

  // Calculate minimum deductible: MAX($1,500, 1% Coverage E, All-Peril)
  const onePercentCoverage = Math.round(coverageE * 0.01);
  const calculatedMinimum = Math.max(1500, onePercentCoverage, deductibleAllOther);

  // Standard percentage options with calculated dollar amounts
  // Note: These must align with SUPPORTED_WINDHAIL_DEDUCTIBLE_VALUES in property-risk-answers.handler.ts
  const percentageOptions = [
    { id: 0.01, percentage: 1 },
    { id: 0.015, percentage: 1.5 },
    { id: 0.02, percentage: 2 }
  ];

  return percentageOptions.map((option) => {
    const dollarAmount = Math.round(coverageE * (option.percentage / 100));
    const actualAmount = Math.max(dollarAmount, calculatedMinimum);
    const isForced = actualAmount > dollarAmount;

    return {
      id: option.id,
      value: isForced
        ? `${option.percentage}% (Min: $${actualAmount.toLocaleString()})`
        : `${option.percentage}% ($${actualAmount.toLocaleString()})`
    };
  });
};

/**
 * Checks if wind & hail should be disabled (when All-Peril > 2% of coverage)
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {boolean} params.isDwellingContents - Whether structure is dwelling/contents type
 * @returns {boolean} Whether wind hail should be disabled
 */
export const isWindHailDisabled = ({ coverages, isDwellingContents }) => {
  const coverageE = isDwellingContents ? coverages?.dwellingLimit || 0 : coverages?.coverageE || 0;
  const deductibleAllOther = coverages?.deductibleAllOther ?? 1500;
  const twoPercentCoverage = coverageE * 0.02;

  return deductibleAllOther > twoPercentCoverage;
};

/**
 * Role-based access control helper - checks if user is underwriter
 * @param {Object} session - User session object
 * @returns {boolean} Whether user has underwriter role
 */
export const isUnderwriter = (session) => {
  if (!session?.user) {
    return false;
  }

  const role = session.user.agencyRole?.toLowerCase();
  // Fix: Use Array.isArray() to safely handle null/undefined permissionGroups
  const groups = Array.isArray(session.user.permissionGroups)
    ? session.user.permissionGroups.map((g) => g?.toLowerCase?.() || '').filter(Boolean)
    : [];

  return role === 'underwriter' || groups.includes('underwriter');
};

/**
 * Role-based access control helper - checks if user is agent
 * @param {Object} session - User session object
 * @returns {boolean} Whether user has agent role
 */
/**
 * Gets role-based loss settlement options for structure types requiring role-based access control
 * @param {string} structureType - Type of structure ('storageGrain', 'confinement', 'poultry')
 * @param {Object} session - User session object
 * @returns {Array} Array of loss settlement options based on user role
 */
export const getRoleBasedLossSettlementOptions = (structureType, session) => {
  // Default to most restrictive permissions if no session
  if (!session) {
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Check user roles - functions already have null safety
  if (isAgent(session)) {
    // Agents: ACV only for all restricted structure types
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  if (isUnderwriter(session)) {
    // Underwriters: Both ACV and RC options
    const rcLabel =
      structureType === 'confinement' || structureType === 'poultry'
        ? 'Replacement cost (Requires UW Approval)'
        : 'Replacement cost';

    return [
      { id: 'ACV', value: 'Actual cash value' },
      { id: 'RC', value: rcLabel }
    ];
  }

  // Fallback for other roles (admins, super-users, etc.)
  // Default to most restrictive permissions for safety
  return [{ id: 'ACV', value: 'Actual cash value' }];
};

/**
 * Gets loss settlement options based on structure type, barn type, and user session
 *
 * This is the main entry point for structure loss settlement options, implementing
 * all business rules for different structure categories and role-based access control.
 *
 * Structure Category Business Rules:
 * - Dwelling/Contents, Hoop Buildings, Fencing: Always ACV only (no RC allowed)
 * - Silos/Grain Bins: Role-based (Agents get ACV only, Underwriters get both)
 * - Confinement/Poultry Buildings: Role-based (Agents get ACV only, Underwriters get both)
 * - Barns/Outbuildings: Building class determines options (1/2=RC only, 3=ACV only)
 *
 * @param {Object} params - Parameters object
 * @param {boolean} params.isDwellingContents - Whether structure is dwelling/contents
 * @param {boolean} params.isHoopBuilding - Whether structure is hoop building
 * @param {boolean} params.isFencing - Whether structure is fencing
 * @param {boolean} params.isStorageGrain - Whether structure is storage/grain (Silos/Grain Bins)
 * @param {boolean} params.isConfinementBuilding - Whether structure is confinement building
 * @param {boolean} params.isPoultryBuilding - Whether structure is poultry building
 * @param {boolean} params.isBarnOutbuilding - Whether structure is barn/outbuilding
 * @param {string|null} params.buildingClass - Building class ('1', '2', '3') for barn/outbuilding structures
 * @param {Object} params.session - User session object containing role information
 * @returns {Array} Array of loss settlement options with id/value pairs
 */
export const getLossSettlementOptions = ({
  isDwellingContents,
  isHoopBuilding,
  isFencing,
  isStorageGrain,
  isConfinementBuilding,
  isPoultryBuilding,
  isBarnOutbuilding,
  buildingClass,
  session
}) => {
  // Business Rule (b), (d), (c): Dwelling/Contents, Hoop Buildings, Fencing always ACV only
  // These structure types do not qualify for Replacement Cost coverage under any circumstances
  if (isDwellingContents || isHoopBuilding || isFencing) {
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Business Rule (f): Silos/Grain Bins - Role-based access control
  // Underwriters can offer RC, Agents restricted to ACV only
  if (isStorageGrain) {
    return getRoleBasedLossSettlementOptions('storageGrain', session);
  }

  // Business Rule (a), (e): Confinement Buildings, Poultry Buildings - Role-based access control
  // Both structure types support RC but require underwriter approval
  if (isConfinementBuilding || isPoultryBuilding) {
    const structureType = isConfinementBuilding ? 'confinement' : 'poultry';
    return getRoleBasedLossSettlementOptions(structureType, session);
  }

  // Business Rule (g): Barns/Outbuildings - Building Class determines coverage options
  // This logic overrides role-based access control for barn structures
  if (isBarnOutbuilding) {
    if (!buildingClass) {
      // Building class not yet determined - show both options to avoid blocking user
      // Once building characteristics are filled, proper restrictions will apply
      return [
        { id: 'ACV', value: 'Actual cash value' },
        { id: 'RC', value: 'Replacement cost' }
      ];
    }

    if (buildingClass === '1' || buildingClass === '2') {
      // Building Class 1 & 2: Must use RC (required for special Building Class 1 coverages)
      // For Building Class 1 & 2, spell out "Replacement Cost" in full (not abbreviated as "RC")
      return [{ id: 'RC', value: 'Replacement Cost' }];
    }

    if (buildingClass === '3') {
      // Building Class 3: ACV only (older buildings not eligible for RC)
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    // Fallback for invalid building class values
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Default for other structure types
  return [
    { id: 'RC', value: 'Replacement cost' },
    { id: 'ACV', value: 'Actual cash value' },
    { id: 'FUNC', value: 'Functional replacement cost' }
  ];
};

/**
 * Get roof loss settlement options for FO-6 structures
 * Roof loss settlement is restricted to only ACV and RC options
 * @param {Object} params - Parameters object
 * @param {string} structureLossSettlement - Current structure loss settlement value
 * @param {string} buildingClass - Building class ('1', '2', '3') for barn/outbuilding structures
 * @param {boolean} isBarnOutbuilding - Whether structure is barn/outbuilding type
 * @returns {Array} Available roof loss settlement options
 */
export const getRoofLossSettlementOptions = ({ structureLossSettlement, buildingClass, isBarnOutbuilding }) => {
  // For barn outbuildings, prioritize building class logic over current structure settlement
  // This prevents timing issues during class transitions (e.g., 3→1 where buildingClass updates before structureLossSettlement)
  if (isBarnOutbuilding && buildingClass) {
    if (buildingClass === '3') {
      // Building Class 3 barns: ACV only
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    if (buildingClass === '1' || buildingClass === '2') {
      // Building Class 1 & 2 barns: Offer RC with ACV for Wind/Hail (default for Type 1/2, like FO3 dwelling)
      // and also plain ACV option
      return [
        { id: 'RC_ACV_WIND_HAIL', value: 'Replacement cost with ACV for wind/hail' },
        { id: 'ACV', value: 'Actual cash value' }
      ];
    }
  }

  // General constraint: When structure is ACV, only offer ACV for roof (RC is not allowed)
  if (structureLossSettlement === 'ACV') {
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // For all other cases (RC, FUNC, null, undefined), offer both options
  return [
    { id: 'ACV', value: 'Actual cash value' },
    { id: 'RC', value: 'Replacement cost' }
  ];
};

/**
 * Validate roof loss settlement business rules
 * @param {Object} coverages - Coverage values object
 * @returns {Object} Validation result { isValid: boolean, message: string }
 */
export const validateRoofLossSettlement = (coverages) => {
  const { structureLossSettlement, roofLossSettlement } = coverages || {};

  // Rule: When structure is ACV, roof cannot be RC or RC_ACV_WIND_HAIL
  if (structureLossSettlement === 'ACV' && (roofLossSettlement === 'RC' || roofLossSettlement === 'RC_ACV_WIND_HAIL')) {
    return {
      isValid: false,
      message:
        'Roof loss settlement cannot be Replacement Cost (including RC with ACV for Wind/Hail) when structure loss settlement is ACV'
    };
  }

  return { isValid: true, message: '' };
};

export const isAgent = (session) => {
  if (!session?.user) {
    return false; // Deny access if no session
  }

  const role = session.user.agencyRole?.toLowerCase();
  return role === 'agent';
};

/**
 * Validates roof year against business rules
 * @param {string|number} roofYear - Roof year value
 * @param {string|number} yearBuilt - Building year built value
 * @returns {Object} Validation result with isValid and message
 */
export const validateRoofYear = (roofYear, yearBuilt) => {
  if (!roofYear) {
    return { isValid: true, message: '' };
  }

  const currentYear = new Date().getFullYear();
  const roofYearNum = parseInt(roofYear, 10);
  const yearBuiltNum = parseInt(yearBuilt, 10);

  // Check for future year
  if (roofYearNum > currentYear) {
    return {
      isValid: false,
      message: `Roof year cannot be in the future (max: ${currentYear})`
    };
  }

  // Check against year built
  if (yearBuilt && roofYearNum < yearBuiltNum) {
    return {
      isValid: false,
      message: `Roof year cannot be before building was built (${yearBuilt})`
    };
  }

  return { isValid: true, message: '' };
};
